import { apiClient } from './apiClient';

export type CriteriaFitItem = {
  confidence: number;
  explanation: string;
  explanation_html?: string;
  criterion_key: string;
  criterion_value: string;
  // Set when a user edits the cell by hand. Availability and price are sourced from
  // Salesforce rather than MLE (BPP-265), so the renderer needs to tell a deliberate
  // edit apart from leftover generated text — including on records generated before
  // that change, which carry no marker and therefore defer to Salesforce.
  explanation_source?: 'user';
};

export type OnePagerWarning = {
  message: string;
  category: string;
  severity: string;
};

export type OnePagerHighlightedEvidence = {
  title: string;
  content: string;
  source_id: string;
  evidence_id: string;
  evidence_type: string;
  relevance_score: number;
};

export type InterimAvailability = {
  start_date?: string;
  onsite_commitment?: string;
  location_flexibility?: string;
  term?: string;
};

export type InterimLocation = {
  base_city?: string;
  current_city?: string;
  relocating?: boolean;
};

export type InterimFees = {
  rate_low?: number;
  rate_high?: number;
  unit?: 'hour' | 'week';
  note?: string;
  visible?: boolean;
};

export type OnePagerContent = {
  concierge_id?: string;
  criteria_fit?: CriteriaFitItem[];
  fit_explanation?: string;
  fit_explanation_html?: string;
  key_alignment_points?: string[] | string;
  project_overview?: string;
  project_overview_html?: string;
  snapshot_html?: string;
  highlighted_evidence?: OnePagerHighlightedEvidence[];
  warnings?: OnePagerWarning[];
  // Interim one-pager fields (BPP-148) — populated from /generate_interim_one_pager
  general_bio?: string;
  snapshot?: string;
  why_we_recommend?: string;
  availability?: InterimAvailability;
  location?: InterimLocation;
  fees?: InterimFees;
  resume_url?: string;
  linkedin_url?: string;
  missing_inputs?: string[];
  confidence_score?: number;
  model_version?: string;
  prompt_version?: string;
};

export type OnePagerType = 'interim' | 'group';

export type OnePagerVersion = {
  id: string;
  accountId: string;
  introductionId: string;
  version: number;
  status: string;
  content: OnePagerContent;
  mleModelVersion: string;
  createdBy: string;
  createdByName?: string;
  updatedBy: string;
  showLogo: boolean;
  initials?: string;
  onePagerType?: OnePagerType;
  dismissedWarnings?: string[];
  createdAt: string;
  updatedAt: string;
};

export type OnePagerVersionsResponse = {
  accountId: string;
  introductionId: string;
  onePagerType?: OnePagerType;
  versions: OnePagerVersion[];
};

export type CreateOnePagerVersionStatus = 'Draft' | 'Published';

export type PdfStatusResponse = {
  pdfStatus: 'processing' | 'success' | 'failed' | null;
  pdfError: string | null;
  blobUrl: string | null;
  fileName: string | null;
  submittedAt: string | null;
};

export type CreateOnePagerVersionRequest = {
  content: Record<string, unknown>;
  status: CreateOnePagerVersionStatus;
  userId?: string;
  showLogo?: boolean;
  initials?: string;
};

export type SendEmailRequest = {
  conciergeId: string;
  emailBody?: string;
  createdByContactId?: string;
};

export type SendEmailResponse = {
  magicLinkUrl: string;
  pdfUrl: string;
  emailsSent: number;
  emailsFailed: number;
};

// One row per introduction (provider one-pager) in a concierge project (BPP-228).
export type ConciergeProviderOnePager = {
  introductionId: string;
  accountId: string;
  providerName: string;
  tagline: string;
  published: boolean;
  hasPdf: boolean;
  latestPublishedVersionId: string | null;
  // Sort keys for the send modal's Provider Descriptions list (BPP-249). Both are null
  // when unavailable — sfStatus if Salesforce has none, latestPublishedAt if the provider
  // has no published one-pager.
  sfStatus: string | null;
  latestPublishedAt: string | null;
};

export type ConciergeProvidersResponse = {
  conciergeId: string;
  providers: ConciergeProviderOnePager[];
};

export type SendProjectEmailRequest = {
  conciergeId: string;
  emailIntro: string;
  // Must include the primary provider; order is primary first, then peers in list order.
  providers: { introductionId: string; blurb: string }[];
  createdByContactId?: string;
};

export type ProjectEmailProviderPayload = {
  introduction_id: string;
  account_id: string;
  provider_name: string;
  role: string;
  is_primary: boolean;
  blurb: string;
  one_pager_pdf: string;
  one_pager_version_id: string;
};

// Pre-Customer.io: the backend validates and returns the assembled payload; nothing is sent.
export type SendProjectEmailResponse = {
  validated: true;
  payload: {
    concierge_id: string;
    project_name: string;
    recipient_names: string;
    client_contacts: string;
    project_associate_name: string;
    project_associate_email: string;
    project_lead_name: string;
    project_lead_email: string;
    account_manager_name: string;
    account_manager_email: string;
    email_intro: string;
    one_pager_count: number;
    providers: ProjectEmailProviderPayload[];
  };
};

const API_V1 = '/api/v1';

export const onePagersService = {
  listVersions(accountId: string, introductionId: string, args?: { token?: string; signal?: AbortSignal }) {
    return apiClient.getJson<OnePagerVersionsResponse>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions`,
      {
        headers: {
          'x-user-token': args?.token,
        },
        signal: args?.signal,
      }
    );
  },

  createVersion(
    accountId: string,
    introductionId: string,
    body: CreateOnePagerVersionRequest,
    args?: { token?: string; signal?: AbortSignal }
  ) {
    return apiClient.postJson<OnePagerVersion>(`${API_V1}/one-pagers/${accountId}/${introductionId}/versions`, body, {
      headers: {
        'x-user-token': args?.token,
      },
      signal: args?.signal,
    });
  },

  triggerPdfGeneration(accountId: string, introductionId: string, versionId: string, pdf: string /* raw base64, no data URI prefix */, fileName?: string) {
    return apiClient.postJson<{ status: string; versionId: string; blobUrl: string }>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions/${versionId}/pdf`,
      { pdf, fileName }
    );
  },

  getPdfStatus(accountId: string, introductionId: string, versionId: string) {
    return apiClient.getJson<PdfStatusResponse>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions/${versionId}/pdf`
    );
  },

  sendEmail(accountId: string, introductionId: string, versionId: string, body: SendEmailRequest) {
    return apiClient.postJson<SendEmailResponse>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions/${versionId}/send-email`,
      body
    );
  },

  listConciergeProviders(conciergeId: string, args?: { signal?: AbortSignal }) {
    return apiClient.getJson<ConciergeProvidersResponse>(
      `${API_V1}/one-pagers/concierge/${conciergeId}/providers`,
      { signal: args?.signal }
    );
  },

  sendProjectEmail(
    accountId: string,
    introductionId: string,
    versionId: string,
    body: SendProjectEmailRequest
  ) {
    return apiClient.postJson<SendProjectEmailResponse>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions/${versionId}/send-project-email`,
      body
    );
  },

  dismissWarning(
    accountId: string,
    introductionId: string,
    versionId: string,
    warning: { category?: string; message: string },
  ) {
    return apiClient.postJson<OnePagerVersion>(
      `${API_V1}/one-pagers/${accountId}/${introductionId}/versions/${versionId}/warnings/dismiss`,
      warning
    );
  },

};
